import styled, { keyframes, css } from "styled-components";
import { hexToRgba } from "../HexToRGBA";

// --- ANIMATION KEYFRAMES ---
export const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;

export const toastBounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
`;

// --- STYLED COMPONENTS ---
export const DashboardContainer = styled.div`
  min-height: 100vh;
  color: ${props => props.theme.secondaryBg};
  font-family: ui-sans-serif, system-ui, sans-serif;
  position: relative;
  padding-bottom: 48px;
  background-color: ${props => props.theme.mainBg};
`;

export const ToastNotification = styled.div`
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 50;
  max-width: 384px;
  width: 100%;
  animation: ${toastBounce} 4s infinite ease-in-out;
  border-radius: 8px;
  border: 1px solid ${props => 
    props.type === "error" ? props => props.theme.errorColor : 
    props.type === "warning" ? props => props.theme.warningColor : props => props.theme.successColor
  };
  padding: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3);
  background-color: ${props => props.theme.mainBg};
  transition: all 0.3s ease;
`;

export const ToastHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

export const ToastTitle = styled.h4`
  font-weight: 700;
  font-size: 1rem;
  color: ${props => 
    props.type === "error" ? props => props.theme.errorColor : 
    props.type === "warning" ? props => props.theme.warningColor : props => props.theme.successColor
  };
`;

export const ToastText = styled.p`
  font-size: 0.875rem;
  color: ${props => props.theme.secondaryBg};
  margin-top: 4px;
`;

export const ToastClose = styled.button`
  color: ${props => props.theme.accentColor};
  background: transparent;
  border: none;
  font-size: 0.75rem;
  cursor: pointer;
  &:hover {
    color: ${props => props.theme.secondaryBg};
  }
`;

export const NavHeader = styled.nav`
  border-b: 1px solid #1e293b;
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${props => props.theme.mainBg};
  
  @media (min-width: 768px) {
    padding: 16px 48px;
  }
`;

export const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const LogoSquare = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.25rem;
  box-shadow: 0 4px 6px -1px rgba(255, 105, 120, 0.1);
  background-color: ${props => props.theme.mainColor};
`;

export const LogoText = styled.span`
  font-size: 1.25rem;
  font-weight: 900;
  letter-spacing: 0.05em;
  color: #fff;
`;

export const RoleSwitcher = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 6px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.accentColor};
  background-color: ${props => hexToRgba(props.theme.secondaryBg,0.5)};
`;

export const RoleButton = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.875rem;
  border: none;
  cursor: pointer;
  background: ${props => props.active ? (props.roleType === "consumer" ? props => props.theme.accentColor : props => props.theme.secondaryBg) : "transparent"};
  color: ${props => props.active ? props.theme.mainBg : props.theme.secondaryBg};
  box-shadow: ${props => props.active ? `0 4px 6px -1px ${hexToRgba(props.theme.secondaryBg, 0.2)}` : "none"};
  transition: all 0.3s ease;

  &:hover {
    color: ${props => props.active ? props.theme.secondaryColor : props.theme.mainBg};
  }
`;

export const LayoutGrid = styled.div`
  max-width: 1400px;
  margin: 32px auto 0 auto;
  padding: 0 24px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(12, minmax(0, 1fr));
    padding: 0 48px;
  }
`;

export const Sidebar = styled.aside`
  border-radius: 16px;
  padding: 24px;
  border: 1px solid #1e293b;
  display: flex;
  flex-col: column;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${props => props.theme.mainBg};
  color: ${props => props.theme.secondaryBg};
  @media (min-width: 1024px) {
    grid-column: span 3 / span 3;
  }
`;

export const ProfileBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  color: ${props => props.theme.secondaryBg};
  border-bottom: 1px solid ${props => props.theme.secondaryBg};
  padding-bottom: 24px;
  margin-bottom: 24px;
`;

export const ProfileAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-weight: 700;
  font-size: 1.125rem;
  background-color: ${props => props.roleType === 'consumer' ? `${props => props.theme.mainColor}25` : `${props => props.theme.secondaryColor}25`};
  border: 2px solid ${props => props.roleType === 'consumer' ? props => props.theme.mainColor : props => props.theme.secondaryColor};
`;

export const ProfileImg = styled.img`
display: flex;
width: 100%;
height: 100%;

`

export const ProfileDetails = styled.div``;

export const ProfileName = styled.h3`
  font-weight: 700;
  font-size: 1rem;
  color: ${props => props.theme.secondaryBg};
`;

export const ProfileRole = styled.span`
  text-transform: uppercase;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  color: ${props => props.theme.secondaryColor};
`;

export const NavMenu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const MenuButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  font-weight: 500;
  font-size: 0.875rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.active ? props.theme.secondaryColor : "transparent"};
  color: ${props => props.active ? props.theme.secondaryBg : props.theme.secondaryColor};

  &:hover {
    background: ${props => props.active ? props.theme.secondaryColor : hexToRgba(props.theme.secondaryColor, 0.15)};
    color: ${props => props.theme.secondaryBg};
  }
`;

export const SidebarInfoBox = styled.div`
  margin-top: 32px;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #1e293b;
  background-color: rgba(15, 23, 42, 0.4);
`;

export const InfoTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

export const InfoTitle = styled.h5`
  font-weight: 700;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${props => props.theme.mainColor};
`;

export const InfoText = styled.p`
  font-size: 0.75rem;
  color: ${props => props.theme.secondaryColor};
  line-height: 1.5;
`;

export const MainContent = styled.main`
  display: flex;
  flex-direction: column;
  gap: 32px;

  @media (min-width: 1024px) {
    grid-column: span 9 / span 9;
  }
`;

export const PromoBanner = styled.div`
  border-radius: 16px;
  padding: 32px;
  border: 1px solid ${props => props.theme.secondaryColor};
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 24px;
  background-color: ${props => props.theme.mainBg};
  background-image: radial-gradient(circle at top right, ${props => (hexToRgba(props.theme.secondaryColor, 0.15))}, transparent 60%);

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

export const BannerContent = styled.div``;

export const BannerTag = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 4px 12px;
  border-radius: 9999px;
  background-color: ${props => (hexToRgba(props.theme.secondaryColor, 0.2))};
  color: ${props => props.theme.mainColor};
`;

export const BannerTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 900;
  color: ${props => props.theme.secondaryBg};
  margin-top: 12px;
  
  @media (min-width: 768px) {
    font-size: 1.875rem;
  }
`;

export const BannerDesc = styled.p`
  color: ${props => props.theme.accentColor};
  margin-top: 8px;
  font-size: 0.875rem;
  max-width: 36rem;
  line-height: 1.5;
`;

export const BannerButton = styled.button`
  padding: 12px 24px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.875rem;
  border: none;
  cursor: pointer;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  background-color: ${props => props.theme.mainColor};

  &:hover {
    transform: scale(1.03);
    opacity: 0.95;
  }
`;

export const GridTwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const Card = styled.div`
  border-radius: 16px;
  padding: 24px;
  border: 1px solid ${props => props.theme.secondaryBg};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${props => props.theme.mainBg};
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 16px;
`;

export const CardTitle = styled.h3`
  font-weight: 700;
  font-size: 1.125rem;
  color: ${props => props.theme.secondaryBg};
`;

export const StatusBadge = styled.span`
  font-size: 0.75rem;
  padding: 4px 10px;
  border-radius: 9999px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background-color: ${props => props.active ? props.theme.secondaryBg : props.theme.accentColor};
  color: ${props => props.active ?  props.theme.successColor :  props.theme.errorColor};
  border: 1px solid ${props => props.active ? hexToRgba(props.theme.mainColor, 0.2) : hexToRgba(props.theme.accentColor, 0.2)};
`;

export const CardIconCircle = styled.span`
  width: 40px;
  height: 40px;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.mainBg};
  color: ${props => props.theme.secondaryColor};
  border: 1px solid ${props => (hexToRgba(props.theme.secondaryBg), 0.2)};
`;

export const CardDesc = styled.p`
  font-size: 0.75rem;
  color: ${props => props.theme.accentColor};
  line-height: 1.625;
  margin-bottom: 24px;
`;

export const BalanceBox = styled.div`
  margin: 16px 0;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid ${props => props.theme.secondaryColor};
  background-color:${props => hexToRgba(props.theme.mainBg, 0.15)};
`;

export const BalanceLabel = styled.span`
  font-size: 0.75rem;
  color: ${props => props.theme.accentColor};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: block;
`;

export const BalanceValue = styled.span`
  font-size: 1.875rem;
  font-weight: 900;
  color: ${props => props.theme.secondaryBg};
  display: block;
  margin-top: 4px;
`;

export const BalanceXRP = styled.p`
  font-size: 0.875rem;
  font-weight: 500;
  display: block;
  margin-top: 4px;
  color: ${props => props.theme.secondaryColor}; 
`;

export const ButtonRowGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;


export const AlertButton = styled.button`
  padding: 14px 16px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.75rem;
  border: 1px solid ${props => hexToRgba(props.theme.secondaryColor, 0.15)};
  cursor: pointer;
  color: ${props => props.theme.errorColor};
  background: transparent;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => hexToRgba(props.theme.accentColor, 0.15)};
  }
`;

export const PanelSection = styled.div`
  border-radius: 16px;
  padding: 24px;
  border: 1px solid ${props => hexToRgba(props.theme.secondaryBg, 0.15)};
  background-color: ${props => props.theme.mainBg};
`;

export const PanelFlexHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;

  @media (min-width: 640px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

export const PanelHeadingBlock = styled.div``;

export const PanelTitle = styled.h3`
  font-weight: 700;
  font-size: 1.125rem;
  color: ${props => props.theme.secondaryBg};
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const PanelSubtitle = styled.p`
  font-size: 0.75rem;
  color: ${props => props.theme.secondaryColor};
  margin-top: 4px;
`;

export const WalletExportBox = styled.div`
  margin-top: 24px;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid ${props => props.theme.secondaryColor};
  background-color: ${props => hexToRgba(props.theme.mainBg, 0.15)};
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const WalletGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const WalletKeyWrapper = styled.div``;

export const WalletKeyLabel = styled.span`
  font-size: 10px;
  color: ${props => props.theme.accentColor};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: block;
`;

export const KeyValueBlock = styled.span`
  font-size: 0.75rem;
  color: ${props => props.theme.secondaryBg};
  font-family: monospace;
  display: block;
  user-select: all;
  background-color: ${props => props.theme.mainBg};
  padding: 10px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.secondaryColor};
  margin-top: 4px;
  overflow-x: auto;
  white-space: nowrap;
`;

export const FlexActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${props => props.theme.mainBg};
  padding: 8px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.secondaryColor};
  margin-top: 4px;
`;

export const CompactValue = styled.span`
  font-size: 0.75rem;
  color: ${props => props.theme.secondaryBg};
  font-family: monospace;
  flex: 1;
  overflow-x: auto;
  white-space: nowrap;
`;

export const MiniCopyBtn = styled.button`
  padding: 4px 12px;
  background-color: ${props => props.theme.mainBg};
  border: none;
  font-size: 10px;
  font-weight: 700;
  color: ${props => props.theme.secondaryBg};
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background-color: ${props => props.theme.accentColor};
  }
`;

export const MetaStatusRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
  color: ${props => props.theme.accentColor};
`;

export const DisconnectBtn = styled.button`
  color: ${props => props.theme.errorColor};
  background: transparent;
  border: none;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

export const HorizontalWalletCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid ${props => props.theme.secondaryColor};
  background-color: ${props => hexToRgba(props.theme.accentColor, 0.3)};
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.secondaryBg};
  }
`;

export const WalletProfileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const ChainAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background-color: ${props => props.theme.secondaryBg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.75rem;
  color: ${props => props.theme.secondaryColor};
`;

export const WalletAddressBlock = styled.div``;

export const WalletEndpointText = styled.span`
  font-size: 0.875rem;
  color: ${props => props.theme.secondaryBg};
  font-family: monospace;
  margin-top: 2px;
  display: block;
`;

export const HorizontalActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const PrimaryPill = styled.span`
  font-size: 10px;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 4px 10px;
  border-radius: 9999px;
  background-color:${props => hexToRgba(props.theme.mainColor,0.5)};
  color: ${props => props.theme.successColor};
  border: 1px solid ${props => hexToRgba(props.theme.mainColor,0.2)};
`;

export const DeleteBtnLink = styled.button`
  color: ${props => props.theme.secondaryBg};
  background: transparent;
  border: none;
  font-size: 0.75rem;
  cursor: pointer;
  transition: color 0.15s ease;
  
  &:hover {
    color: ${props => props.theme.errorColor};
  }
`;

export const StyledForm = styled.form`
  margin-top: 24px;
  max-width: 448px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const FormGroup = styled.div``;

export const FormInputLabel = styled.label`
  font-size: 0.75rem;
  color: ${props => props.theme.secondaryColor};
  text-transform: uppercase;
  font-weight: 600;
  display: block;
  margin-bottom: 6px;
`;

export const FormInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  background-color: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.secondaryBg};
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 0.875rem;
  color:${props => props.theme.secondaryBg};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.mainColor};
  }
`;

export const FormSelect = styled.select`
  width: 100%;
  box-sizing: border-box;
  background-color: ${props => props.theme.mainBg};
  border: 1px solid ${props => props.theme.secondaryBg};
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 0.875rem;
  color: ${props => props.theme.secondaryBg};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.secondaryColor};
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: ${props => hexToRgba(props.theme.mainBg,0.15)};
  backdrop-filter: blur(4px);
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

export const ModalBox = styled.div`
  max-width: 448px;
  width: 100%;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid ${props => props.theme.secondaryColor};
  position: relative;
  box-shadow: 0 25px 50px -12px ${props => hexToRgba(props.theme.background,0.55)};
  animation: ${fadeIn} 0.2s ease-out;
  background-color: ${props => props.theme.secondaryBg};
`;

export const ModalCloseBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  color: ${props => props.theme.secondaryColor};
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  
  &:hover {
    color: ${props => props.theme.secondaryColor};
  }
`;

export const SegmentedControl = styled.div`
  display: flex;
  gap: 8px;
  background-color: ${props => props.theme.mainBg};
  padding: 6px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.secondaryBg};
  margin-bottom: 24px;
`;

export const SegmentBtn = styled.button`
  flex: 1;
  padding: 8px;
  border-radius: 4px;
  font-weight: 700;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.active ? props.theme.mainBg : "transparent"};
  color: ${props => props.active ?  props.theme.secondaryBg : props.theme.secondaryColor};

  &:hover {
    color: ${props => props.theme.secondaryBg};
  }
`;

export const InputPrefixGroup = styled.div`
  position: relative;
`;

export const InputPrefixText = styled.span`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-weight: 700;
  color: ${props => props.theme.accentColor};
  font-size: 0.875rem;
`;

export const IndentedInput = styled(FormInput)`
  padding-left: 32px;
  font-weight: 700;
`;

export const AlertNotice = styled.div`
  padding: 16px;
  border-radius: 12px;
  border: 1px solid ${props => props.theme.accentColor};
  background-color: ${props => hexToRgba(props.theme.mainBg,0.5)};
  color: ${props => props.theme.secondaryBg};
  font-size: 0.75rem;
  line-height: 1.5;
  margin-bottom: 24px;
  
  strong {
    color: ${props => props.theme.secondaryBg};
  }
`;



// --- INLINE SVG ICONS ---
export const WalletIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12a2 2 0 0 0 0 4h4v-4Z"/></svg>
);
export const AwardIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="7"/><path d="M8.21 13.89 7 23l5-3 5 3-1.21-9.12"/></svg>
);
export const CoinsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="8" r="6"/><circle cx="18" cy="18" r="4"/><path d="M12 18a6 6 0 0 0-6-6"/><path d="M14 8h8"/></svg>
);
export const SettingsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.1a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>
);
export const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
);
export const SparklesIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>
);
export const MusicIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
);
